import {createSelector} from '@reduxjs/toolkit'

import {reduceValueKeys} from '@shared/helpers/object'

import {
    CouponDiscount,
    CouponDiscountType,
    CouponProvider,
    CouponProviderType,
    DiscountCoupon,
    DiscountCouponType,
} from '@web/core/constants'
import {orderSheetCouponSelector} from '@web/slices/orderSheetCoupon/slice'
import {orderTotalSelector as orderTotalSliceSelector} from '@web/slices/orderTotal/slice'

type DiscountAmountByDiscountType = Record<DiscountCouponType, number>

type DiscountAmountInfo = {
    amountByDiscountType: DiscountAmountByDiscountType
    total: number
}

const getInitialDiscountAmountInfo = (): DiscountAmountInfo => {
    const amountByDiscountType = Object.values(DiscountCoupon).reduce((acc, key) => {
        acc[key] = 0

        return acc
    }, {} as DiscountAmountByDiscountType)

    return {
        amountByDiscountType,
        total: 0,
    }
}

const getDiscountCoupon = (
    discountType: CouponDiscountType,
    discountProviderType: CouponProviderType,
): DiscountCouponType => {
    switch (discountType) {
        case CouponDiscount.DELIVERY_FEE_DISCOUNT:
            return DiscountCoupon.DELIVERY_DISCOUNT
        case CouponDiscount.DUPLICATE_PRODUCT_DISCOUNT:
            return DiscountCoupon.DUPLICATE_PRODUCT_DISCOUNT
        case CouponDiscount.IMMEDIATE_DISCOUNT:
            return discountProviderType === CouponProvider.MANAGER
                ? DiscountCoupon.MANAGER_IMMEDIATE_DISCOUNT
                : DiscountCoupon.SELLER_IMMEDIATE_DISCOUNT
        case CouponDiscount.MANAGER_PRODUCT_DISCOUNT:
            return DiscountCoupon.MANAGER_PRODUCT_DISCOUNT
        case CouponDiscount.MERCHANT_PRODUCT_DISCOUNT:
            return DiscountCoupon.SELLER_PRODUCT_DISCOUNT
        case CouponDiscount.MULTI_PURCHASE_DISCOUNT:
            return DiscountCoupon.MULTI_PURCHASE_DISCOUNT
        case CouponDiscount.PRODUCT_DISCOUNT:
            return discountProviderType === CouponProvider.MANAGER
                ? DiscountCoupon.MANAGER_PRODUCT_DISCOUNT
                : DiscountCoupon.SELLER_PRODUCT_DISCOUNT
        case CouponDiscount.SELLER_IMMEDIATE_DISCOUNT:
            return DiscountCoupon.SELLER_IMMEDIATE_DISCOUNT
        case CouponDiscount.STORE_DISCOUNT:
            return DiscountCoupon.STORE_DISCOUNT
        case CouponDiscount.SUBSCRIPTION_DISCOUNT:
            return DiscountCoupon.SUBSCRIPTION_DISCOUNT
    }
}

export const orderTotalSelector = createSelector(
    orderTotalSliceSelector,
    orderSheetCouponSelector,
    ({amount, deliveryFeeAmount}, {discount}) => {
        const productDiscountAmountInfo = reduceValueKeys(
            (acc, discountInfoList) => {
                return discountInfoList.reduce((innerAcc, {discountAmount, discountType, discountProviderType}) => {
                    innerAcc.amountByDiscountType[getDiscountCoupon(discountType, discountProviderType)] +=
                        discountAmount
                    innerAcc.total += discountAmount

                    return innerAcc
                }, acc)
            },
            getInitialDiscountAmountInfo(),
            discount.productItem,
        )

        const deliveryDiscountAmount = reduceValueKeys(
            (acc, deliveryDiscount) => {
                return acc + deliveryDiscount.discountAmount
            },
            0,
            discount.deliveryItem ?? {},
        )

        return {
            /** 할인이 적용되지 않은 주문 총액 */
            orderAmount: amount,
            /** 배송비 */
            deliveryFeeAmount,
            /** 배송비 할인 총액 */
            deliveryDiscountAmount,
            /** 상품 할인 종류별 할인액 총합 */
            productDiscountAmountByDiscountType: productDiscountAmountInfo.amountByDiscountType,
            /** 할인 총액 */
            totalDiscountAmount: productDiscountAmountInfo.total + deliveryDiscountAmount,
        }
    },
)
